import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { isAxiosError } from "axios";
import { Platform } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "@repo/core/hooks/useSearchParams";
import { Flow } from "@repo/core/services/entities/Unico";
import { BIOMETRY_UNICO_PARAM_GROUP } from "@repo/core/constants/biometry-reuse-wayup";
import { UNICO_CALLBACK_URL } from "@repo/core/constants/global";
import { CommonResource } from "@repo/core/services/resources/common";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { apiInstance } from "@repo/core/services/instances/api";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { useUnicoCreateProcess } from "@repo/core/hooks/useUnicoCreateProcess";
import { useBiometryRegister } from "@repo/core/hooks/useBiometryRegister";
import { BIOMETRICS_FLOW } from "@repo/core/services/entities/Biometrics";
import { LastCaptureResponseDTO } from "@repo/core/services/entities/Proposals";
import type {
  BiometryValidityParamsResponseDTO,
  BioTokenIdReferenceResponseDTO,
  UnicoStagesPerformedResponseDTO
} from "@repo/core/services/entities/WayupRequisitions";
import useWayupApiAuthStore from "@repo/core/stores/wayupApiAuth";
import {
  PreProposalData,
  RequiredDocuments
} from "../../../store/preProposalStore";
import { usePreProposal } from "../../../hooks/usePreProposal";
import { UnicoProcessCallbacks } from "../types";
import { STATUS_MESSAGES } from "../constants";
import { WEB_BIOMETRY_STORAGE } from "../utils";
import {
  createUnicoProcessPayload,
  createCallbackUri,
  formatCpfForUnico,
  resolveBioTokenIdForIdTokenFlow
} from "../helpers/unicoPayload.helper";
import {
  parseCacheConsultasDays,
  pickProcessOidFromUnicoStages,
  daysToLimiteMinutos
} from "../helpers/biometryReuseWayup.helpers";
import {
  determineUnicoFlow,
  shouldRetryProcess,
  ExistUnicoRecord,
  requiresIdentityDocumentCapture,
  hasUploadedIdentityDocuments,
  shouldRunUnicoProcessForRequiredDocuments,
  getCachedPositiveExistUnicoRecord,
  shouldWaitForExistUnicoRecordValidation,
  getEffectiveExistUnicoRecordForFlow,
  shouldApplyForcedBiometryFlowOverride
} from "../services/unicoProcess.service";

const commonResource = new CommonResource(apiInstance);
const proposalsResourceNewApi = new ProposalsResource(newApInstance);
const requisitionsWayupResource = new RequisitionsResource(wayupApiInstance);

const shouldRetryWayupStages = (failureCount: number, error: unknown) => {
  if (isAxiosError(error) && error.response?.status === 404) {
    return false;
  }

  return failureCount < 3;
};

const isTimeoutError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }

  return (
    error.code === "ECONNABORTED" ||
    /timeout/i.test(error.message || "") ||
    error.response?.status === 408
  );
};

const shouldRetryOnlyOnTimeout = (failureCount: number, error: unknown) => {
  return isTimeoutError(error) && failureCount < 3;
};

interface UseUnicoProcessParams {
  proposalData: PreProposalData | null;
  setStatusMessage: (message: string) => void;
  callbacks?: UnicoProcessCallbacks;
  requiredDocuments?: RequiredDocuments[] | null;
  isFetchedRequiredDocuments?: boolean;
}

interface CreateUnicoProcessOptions {
  forceNewProcess?: boolean;
}

export const useUnicoProcess = ({
  proposalData,
  setStatusMessage,
  callbacks,
  requiredDocuments,
  isFetchedRequiredDocuments = false
}: UseUnicoProcessParams) => {
  const searchParams = useSearchParams();
  const { registerBiometryStepLog } = useBiometryRegister();
  const { setProposalData: setProposalDataInStore } = usePreProposal();
  const hasInitiatedUnicoProcess = useRef(false);
  const hasHandledError = useRef(false);
  const unicoRetryCount = useRef(0);
  const pendingVerificationId = useRef<string | null>(null);

  const [validationURL, setValidationURL] = useState("");
  const [processId, setProcessId] = useState("");
  const [token, setToken] = useState("");
  const [isFinishedValidation, setIsFinishedValidation] = useState(false);

  const wayupAccessToken = useWayupApiAuthStore((s) => s.accessToken);
  const hasWayupToken = !!wayupAccessToken;

  const lngProposta = proposalData?.lngProposta;

  const hasVerificationId = !!searchParams?.verificationId;
  const shouldForceNewBiometry = proposalData?.shouldForceNewBiometry === true;
  const shouldRunUnicoProcess =
    hasVerificationId ||
    (isFetchedRequiredDocuments &&
      shouldRunUnicoProcessForRequiredDocuments(requiredDocuments));
  const explicitBiometryReuseProcessId =
    !shouldForceNewBiometry && proposalData?.biometryReuseProcessId
      ? String(proposalData.biometryReuseProcessId).trim()
      : "";
  const shouldWaitForExistUnicoRecord = shouldWaitForExistUnicoRecordValidation(
    !!explicitBiometryReuseProcessId
  );

  const cachedExistUnicoRecord = getCachedPositiveExistUnicoRecord(
    proposalData?.blnExisteRegistroUnico
  );

  const {
    data: existUnicoRecordResponse,
    isFetched: isFetchedExistUnicoRecordFromQuery,
    isFetching: isFetchingExistUnicoRecordFromQuery
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["existUnicoRecord", proposalData?.strCliente],
    queryFn: async () => {
      if (hasVerificationId) {
        return null;
      }
      const response = await commonResource.existUnicoRecord(
        proposalData?.strCliente || ""
      );
      if (response?.data) {
        setProposalDataInStore(
          {
            blnExisteRegistroUnico: response.data.blnExisteRegistro
          },
          { skipAlterarSync: true }
        );
      }
      return response.data;
    },
    enabled:
      shouldRunUnicoProcess &&
      !hasVerificationId &&
      !cachedExistUnicoRecord &&
      !!proposalData?.strCliente,
    refetchOnMount: "always"
  });

  const existUnicoRecord = cachedExistUnicoRecord || existUnicoRecordResponse;
  const isFetchedExistUnicoRecord = cachedExistUnicoRecord
    ? true
    : isFetchedExistUnicoRecordFromQuery &&
      !isFetchingExistUnicoRecordFromQuery;

  const reuseGateCommon =
    shouldRunUnicoProcess &&
    !hasVerificationId &&
    !!lngProposta &&
    isFetchedExistUnicoRecord &&
    !shouldForceNewBiometry &&
    !explicitBiometryReuseProcessId;

  const isLastBiometryProcessResultEnabled =
    !hasVerificationId &&
    shouldRunUnicoProcess &&
    !shouldForceNewBiometry &&
    !explicitBiometryReuseProcessId &&
    !!proposalData?.strCliente &&
    !!existUnicoRecord?.blnExisteRegistro &&
    isFetchedExistUnicoRecord;

  const isBioTokenIdReferenceEnabled =
    isLastBiometryProcessResultEnabled && hasWayupToken;

  const {
    data: prefetchedBioTokenIdReference,
    isFetched: isFetchedBioTokenIdReference
  } = useQuery({
    queryKey: ["bioTokenIdReference", proposalData?.strCliente],
    queryFn: () =>
      requisitionsWayupResource.getBioTokenIdReference(
        formatCpfForUnico(proposalData?.strCliente || "")
      ),
    select: (data) => data.data as BioTokenIdReferenceResponseDTO,
    enabled: isBioTokenIdReferenceEnabled,
    retry: shouldRetryOnlyOnTimeout,
    staleTime: Infinity
  });

  const prefetchedBioTokenId = useMemo(() => {
    const processoid =
      prefetchedBioTokenIdReference?.processoid != null
        ? String(prefetchedBioTokenIdReference.processoid).trim()
        : "";
    const valida = String(prefetchedBioTokenIdReference?.valida ?? "")
      .trim()
      .toUpperCase();

    return processoid !== "" && valida === "S" ? processoid : undefined;
  }, [prefetchedBioTokenIdReference]);

  const wayupValidityEnabled = reuseGateCommon && hasWayupToken;

  const { data: wayupValidityRows, isFetched: wayupValidityFetched } = useQuery(
    {
      queryKey: ["wayupBiometryValidityParams", BIOMETRY_UNICO_PARAM_GROUP],
      queryFn: async () => {
        const res = await requisitionsWayupResource.getBiometryValidityParams(
          BIOMETRY_UNICO_PARAM_GROUP
        );
        return res.data as BiometryValidityParamsResponseDTO;
      },
      enabled: wayupValidityEnabled,
      staleTime: Infinity
    }
  );

  const reuseCacheDays = useMemo(() => {
    if (!hasWayupToken) {
      return parseCacheConsultasDays(undefined);
    }
    if (!wayupValidityFetched) {
      return parseCacheConsultasDays(undefined);
    }
    return parseCacheConsultasDays(wayupValidityRows);
  }, [hasWayupToken, wayupValidityFetched, wayupValidityRows]);

  const limiteMinutos = useMemo(
    () => daysToLimiteMinutos(reuseCacheDays),
    [reuseCacheDays]
  );

  const reuseParamsReady =
    reuseGateCommon && (!hasWayupToken || wayupValidityFetched);

  const wayupStagesEnabled = reuseParamsReady && hasWayupToken;

  const { data: wayupStagesRows, isFetched: wayupStagesFetched } = useQuery({
    queryKey: ["wayupUnicoStagesPerformed", lngProposta, limiteMinutos],
    queryFn: async () => {
      const res = await requisitionsWayupResource.getUnicoStagesPerformed(
        lngProposta!,
        limiteMinutos
      );
      return res.data as UnicoStagesPerformedResponseDTO;
    },
    enabled: wayupStagesEnabled && !!lngProposta && !!limiteMinutos,
    retry: shouldRetryWayupStages,
    staleTime: Infinity
  });

  const wayupReuseProcessOid = useMemo(() => {
    if (!hasWayupToken || !wayupStagesFetched) {
      return undefined;
    }
    return pickProcessOidFromUnicoStages(wayupStagesRows);
  }, [hasWayupToken, wayupStagesFetched, wayupStagesRows]);

  const nestReuseEnabled =
    reuseParamsReady &&
    (!hasWayupToken ||
      (wayupStagesEnabled && wayupStagesFetched && !wayupReuseProcessOid));

  const { data: nestLastCaptureData, isFetched: nestLastCaptureFetched } =
    useQuery({
      queryKey: ["lastCapture", lngProposta, limiteMinutos],
      queryFn: () =>
        proposalsResourceNewApi.getLastCapture(String(lngProposta || ""), {
          limiteMinutos
        }),
      select: (data) =>
        data.data as LastCaptureResponseDTO | Record<string, never>,
      enabled: !!lngProposta && !!limiteMinutos && nestReuseEnabled,
      staleTime: Infinity
    });

  const reuseFlowReady =
    reuseParamsReady &&
    (!hasWayupToken || wayupStagesFetched) &&
    (!nestReuseEnabled || nestLastCaptureFetched);

  const effectiveReuseOid = useMemo(() => {
    const fromWayup =
      typeof wayupReuseProcessOid === "string"
        ? wayupReuseProcessOid.trim()
        : "";
    const fromNest = nestLastCaptureFetched
      ? String(
          (nestLastCaptureData as LastCaptureResponseDTO | undefined)
            ?.processoid ?? ""
        ).trim()
      : "";

    return fromWayup || fromNest || undefined;
  }, [wayupReuseProcessOid, nestLastCaptureFetched, nestLastCaptureData]);

  /**
   * For proposals that still need identity capture, without prior valid Unico
   * record and without uploaded identity docs, force new process creation.
   */
  const shouldSkipReuseForFreshIdentityCapture =
    !!requiredDocuments?.length &&
    requiresIdentityDocumentCapture(requiredDocuments) &&
    !existUnicoRecord?.blnExisteRegistro &&
    !hasUploadedIdentityDocuments(proposalData?.uploadedProposalDocumentTypes);

  const hasReuseProcessId =
    !!explicitBiometryReuseProcessId ||
    (!!effectiveReuseOid &&
      !existUnicoRecord?.blnExisteRegistro &&
      !shouldSkipReuseForFreshIdentityCapture);
  const reuseProcessId =
    explicitBiometryReuseProcessId || effectiveReuseOid || undefined;

  const { createProcess: createUnicoProcess } = useUnicoCreateProcess({
    onSuccess: async (data) => {
      if (hasVerificationId) {
        return;
      }

      await registerBiometryStepLog({
        step: BIOMETRICS_FLOW.CREATE_PROCESS,
        lngProposta: proposalData?.lngProposta || 0,
        response: data
      });

      if (data?.process) {
        const verificationId = pendingVerificationId.current;
        if (!verificationId) {
          callbacks?.onError?.(
            new Error("Missing verification ID for the Unico web callback.")
          );
          return;
        }

        const dataToSave = {
          processId: data.process.id,
          params: {
            callbackUri: createCallbackUri(verificationId, UNICO_CALLBACK_URL),
            flow: data.process.flow as Flow,
            purpose: "creditprocess"
          }
        };
        WEB_BIOMETRY_STORAGE.save(verificationId, dataToSave);

        setProcessId(data.process.id);
        setToken(data.process.token);
        setValidationURL(data.process.userRedirectUrl);
        setStatusMessage(STATUS_MESSAGES.VALIDATING);
      }
    },
    onError: callbacks?.onError,
    onUnavailable: callbacks?.onUnavailable
  });

  const failBioTokenIdResolution = useCallback(
    (error: Error) => {
      hasInitiatedUnicoProcess.current = false;
      callbacks?.onError?.(error);
    },
    [callbacks]
  );

  const handleUnicoCreateProcess = useCallback(
    async (flow: Flow = Flow.iddocs, options?: CreateUnicoProcessOptions) => {
      const forceNewProcess = options?.forceNewProcess === true;

      if (searchParams?.verificationId && !forceNewProcess) {
        setIsFinishedValidation(true);
        setStatusMessage(STATUS_MESSAGES.VERIFYING_RESULT);

        const verificationId = searchParams.verificationId;

        const params = WEB_BIOMETRY_STORAGE.get(verificationId);

        let processIdToUse = params?.processId;

        if (!processIdToUse && proposalData?.strIdProcessoUnico) {
          processIdToUse = proposalData.strIdProcessoUnico;
        }

        if (processIdToUse) {
          setProcessId(processIdToUse);
          callbacks?.onSuccess?.(processIdToUse);
          return;
        } else {
          const processIdFromUrl = searchParams?.processId;
          if (processIdFromUrl) {
            setProcessId(processIdFromUrl);
            callbacks?.onSuccess?.(processIdFromUrl);
            return;
          }

          callbacks?.onSuccess?.("");

          if (hasHandledError.current) {
            return;
          }
          hasHandledError.current = true;

          setTimeout(() => {
            hasHandledError.current = false;
          }, 2000);
          return;
        }
      }

      if (hasVerificationId && !forceNewProcess) {
        return;
      }

      if (searchParams?.verificationId && forceNewProcess) {
        WEB_BIOMETRY_STORAGE.remove(searchParams.verificationId);
        if (
          Platform.OS === "web" &&
          typeof window !== "undefined" &&
          window.location.search.includes("verificationId")
        ) {
          window.history.replaceState({}, "", window.location.pathname);
        }
      }

      if (forceNewProcess) {
        hasInitiatedUnicoProcess.current = true;
      }

      const verificationId = WEB_BIOMETRY_STORAGE.generateVerificationId();
      const callbackUri = createCallbackUri(verificationId, UNICO_CALLBACK_URL);
      pendingVerificationId.current = verificationId;

      let flowToCreate = flow;
      let bioTokenId: string | undefined;
      const hasRequiredIdentityDocuments =
        !!requiredDocuments?.length &&
        requiresIdentityDocumentCapture(requiredDocuments);
      const shouldOverrideFlowForForcedBiometry =
        shouldApplyForcedBiometryFlowOverride(
          flowToCreate,
          shouldForceNewBiometry
        );

      if (shouldOverrideFlowForForcedBiometry) {
        flowToCreate = hasRequiredIdentityDocuments
          ? Flow.iddocs
          : Flow.idcheck;
      }

      if (
        !shouldOverrideFlowForForcedBiometry &&
        flowToCreate === Flow.iddocs
      ) {
        if (!hasRequiredIdentityDocuments && prefetchedBioTokenId) {
          bioTokenId = prefetchedBioTokenId;
          flowToCreate = Flow.idcheck;
        } else if (!hasRequiredIdentityDocuments) {
          bioTokenId = await resolveBioTokenIdForIdTokenFlow(
            requisitionsWayupResource,
            proposalData,
            {
              proposalsResource: proposalsResourceNewApi,
              lngProposta: proposalData?.lngProposta,
              limiteMinutos
            }
          );
          if (bioTokenId) {
            flowToCreate = Flow.idcheck;
          }
        }
      }

      if (
        !shouldOverrideFlowForForcedBiometry &&
        (flowToCreate === Flow.idtoken || flowToCreate === Flow.idcheck)
      ) {
        if (!bioTokenId) {
          bioTokenId =
            prefetchedBioTokenId ??
            (await resolveBioTokenIdForIdTokenFlow(
              requisitionsWayupResource,
              proposalData,
              {
                proposalsResource: proposalsResourceNewApi,
                lngProposta: proposalData?.lngProposta,
                limiteMinutos
              }
            ));
        }
        if (flowToCreate === Flow.idtoken && !bioTokenId) {
          failBioTokenIdResolution(
            new Error("Could not resolve bioTokenId for idtoken flow.")
          );
          return;
        }
      }

      const payload = createUnicoProcessPayload(
        proposalData,
        flowToCreate,
        callbackUri,
        bioTokenId ? { bioTokenId } : undefined
      );

      await createUnicoProcess(payload);
    },
    [
      searchParams,
      hasVerificationId,
      setStatusMessage,
      callbacks,
      createUnicoProcess,
      proposalData,
      prefetchedBioTokenId,
      limiteMinutos,
      requiredDocuments,
      failBioTokenIdResolution,
      shouldForceNewBiometry
    ]
  );

  const resetProcessState = () => {
    setProcessId("");
    setToken("");
    setValidationURL("");
    hasInitiatedUnicoProcess.current = false;
  };

  /**
   * Reuses the current process without creating a new one.
   * This is useful when the WebView closes or fails without completing the validation.
   * Instead of creating a new process, it simply reopens the existing WebView URL.
   */
  const reuseCurrentProcess = () => {
    if (processId && validationURL) {
      setIsFinishedValidation(false);
      setStatusMessage(STATUS_MESSAGES.VALIDATING);
      return true;
    }
    return false;
  };

  const handleRetry = async () => {
    if (
      requiredDocuments &&
      requiredDocuments.length > 0 &&
      !shouldRunUnicoProcessForRequiredDocuments(requiredDocuments)
    ) {
      return false;
    }

    // First, try to reuse the current process if it exists
    if (reuseCurrentProcess()) {
      return true;
    }

    // If no current process exists, create a new one (only if retry attempts allow)
    if (shouldRetryProcess(unicoRetryCount.current)) {
      unicoRetryCount.current += 1;
      setIsFinishedValidation(false);
      setStatusMessage(STATUS_MESSAGES.STARTING_PROCESS);
      resetProcessState();

      const resolvedFlow = determineUnicoFlow(
        getEffectiveExistUnicoRecordForFlow(
          existUnicoRecord as ExistUnicoRecord,
          shouldForceNewBiometry
        ),
        requiredDocuments ?? undefined,
        proposalData?.uploadedProposalDocumentTypes
      );
      await handleUnicoCreateProcess(resolvedFlow);
      return true;
    }

    unicoRetryCount.current = 0;
    return false;
  };

  useEffect(() => {
    if (hasVerificationId) {
      if (!hasInitiatedUnicoProcess.current && !hasHandledError.current) {
        hasInitiatedUnicoProcess.current = true;
        handleUnicoCreateProcess();
      }
      return;
    }

    if (hasInitiatedUnicoProcess.current || hasHandledError.current) {
      return;
    }

    if (!isFetchedRequiredDocuments) {
      return;
    }

    if (!shouldRunUnicoProcess) {
      return;
    }

    if (
      shouldWaitForExistUnicoRecord &&
      (!existUnicoRecord || !isFetchedExistUnicoRecord)
    ) {
      return;
    }

    if (proposalData?.unicoStatus === "unavailable") {
      return;
    }

    if (reuseGateCommon && !reuseFlowReady) {
      return;
    }

    if (isBioTokenIdReferenceEnabled && !isFetchedBioTokenIdReference) {
      return;
    }

    if (hasReuseProcessId && reuseProcessId) {
      hasInitiatedUnicoProcess.current = true;
      setProcessId(reuseProcessId);
      setStatusMessage(STATUS_MESSAGES.REUSING_CAPTURE);
      callbacks?.onSuccess?.(reuseProcessId);
      return;
    }

    hasInitiatedUnicoProcess.current = true;
    const resolvedFlow = determineUnicoFlow(
      getEffectiveExistUnicoRecordForFlow(
        existUnicoRecord as ExistUnicoRecord,
        shouldForceNewBiometry
      ),
      requiredDocuments ?? undefined,
      proposalData?.uploadedProposalDocumentTypes
    );
    handleUnicoCreateProcess(resolvedFlow);
  }, [
    hasVerificationId,
    existUnicoRecord,
    isFetchedExistUnicoRecord,
    isFetchedRequiredDocuments,
    reuseGateCommon,
    reuseFlowReady,
    hasReuseProcessId,
    reuseProcessId,
    proposalData?.unicoStatus,
    proposalData?.uploadedProposalDocumentTypes,
    shouldForceNewBiometry,
    shouldRunUnicoProcess,
    shouldWaitForExistUnicoRecord,
    requiredDocuments,
    isBioTokenIdReferenceEnabled,
    isFetchedBioTokenIdReference,
    prefetchedBioTokenId,
    callbacks,
    handleUnicoCreateProcess,
    setStatusMessage
  ]);

  return {
    processId,
    token,
    validationURL,
    isFinishedValidation,
    setIsFinishedValidation,
    handleCreateProcess: handleUnicoCreateProcess,
    handleRetry,
    reuseCurrentProcess,
    resetProcessState,
    registerBiometryStepLog
  };
};
